import { FC, useCallback, useMemo } from 'react';
import { Flex } from 'antd';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  ActionButton,
  confirmationHeaders,
  Form,
  FormAlerts,
  FormField,
  GetApiErrorMessage,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
  useMutationErrors,
  ValidationDashboardUpdateSchema,
  ValidationDashboardUpdateValidFormType,
  ValidationDashboardListDto,
  useMutateValidationDashboardQuery,
  useAlertContext,
} from '@atlas-frontend/sdk';
import translations from './EditDashboardModal.translations.json';

const { addTranslations, t } = i18nNsFactory('edit-dashboard-modal');
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

export type EditDashboardModalProps = {
  validationDashboard: ValidationDashboardListDto;
  onClose: () => void;
  onSuccess: () => void;
};

export const EditDashboardModal: FC<EditDashboardModalProps> = (props) => {
  const { validationDashboard, onClose, onSuccess } = props;

  const { mutateAsync } = useMutateValidationDashboardQuery();

  const { resetAlert, setSuccess } = useAlertContext();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);

  const handleSubmit = useCallback(
    (values: ValidationDashboardUpdateValidFormType, confirm: boolean) => {
      clearMutationErrors();
      resetAlert();
      return mutateAsync({
        id: validationDashboard.id,
        data: values,
        headers: confirmationHeaders(confirm),
      })
        .then(() => {
          setSuccess();
          onSuccess();
        })
        .catch((apiErrorResponse) => {
          setMutationErrors(apiErrorResponse.response?.data.errors || []);
        });
    },
    [
      clearMutationErrors,
      mutateAsync,
      onSuccess,
      setMutationErrors,
      validationDashboard,
      resetAlert,
      setSuccess,
    ],
  );

  const defaultValues = useMemo(
    () => ({
      name: validationDashboard.name,
      description: validationDashboard?.description ?? null,
    }),
    [validationDashboard],
  );

  return (
    <Form
      schema={ValidationDashboardUpdateSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      serverErrors={mutationErrors}
      idAttributePrefix="userFollowedDataObjectDroup"
    >
      <EditDashboardModalInner onClose={onClose} />
    </Form>
  );
};

function EditDashboardModalInner({ onClose }: { onClose: () => void }) {
  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Modal
      footer={
        <ModalFooter>
          <Flex justify="end" gap="small">
            <ActionButton type="abort" onClick={handleCancel} />
            <ActionButton type="formSubmit" />
          </Flex>
        </ModalFooter>
      }
      onCancel={onClose}
      width="30rem"
    >
      <ModalHeader>
        <ModalTitle>{t('title')}</ModalTitle>
      </ModalHeader>
      <FormAlerts />
      <Padding scrollable>
        <Flex vertical gap="middle">
          <FormField name="name" label={t('name.label')} required />
          <FormField name="description" label={t('description.label')} />
        </Flex>
      </Padding>
    </Modal>
  );
}
